import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  X,
  Rocket,
  ArrowRight,
  Minimize2,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useOnboardingTour } from "@/contexts/OnboardingTourContext";
import { cn } from "@/lib/utils";

export function OnboardingTour() {
  const {
    isVisible,
    currentStepIndex,
    currentStep,
    steps,
    totalSteps,
    prevStep,
    nextStep,
    goToStep,
    dismiss,
    isDismissing,
  } = useOnboardingTour();

  const [, navigate] = useLocation();
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Use a ref to track if the dialog was triggered by the "X" button or the "Finish" button
  const [isFinishing, setIsFinishing] = useState(false);

  // Desktop positioning state
  const [activeItemRect, setActiveItemRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  const [positionReady, setPositionReady] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const updatePosition = useCallback(() => {
    const el = document.querySelector("[data-tour-active]");
    if (el) {
      const rect = el.getBoundingClientRect();
      setActiveItemRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
      setPositionReady(true);
    } else {
      setActiveItemRect(null);
    }
  }, []);

  useEffect(() => {
    if (!isVisible || !isDesktop) {
      setActiveItemRect(null);
      setPositionReady(false);
      return;
    }
    setPositionReady(false);
    let count = 0;
    const maxPolls = 15;
    const poll = () => {
      updatePosition();
      count++;
      if (count < maxPolls) {
        rafRef.current = requestAnimationFrame(poll);
      }
    };
    const timeout = setTimeout(() => {
      rafRef.current = requestAnimationFrame(poll);
    }, 150);

    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      clearTimeout(timeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isVisible, isDesktop, currentStepIndex, updatePosition]);

  // ── Dynamic Dialog Content (Moved BEFORE early return) ────────────────────
  const dialogContent = useMemo(() => {
    if (isFinishing) {
      return {
        title: "Finish Getting Started Guide?",
        description: (
          <>
            Great job! You've reached the end. You can view the full step details anytime from the <strong>Setup Guide</strong> page in the sidebar.
          </>
        ),
        confirmLabel: "Finish Guide",
      };
    }
    return {
      title: "Hide Getting Started Guide?",
      description: (
        <>
          You can view the full step details anytime from the <strong>Setup Guide</strong> page in the sidebar. No progress will be lost.
        </>
      ),
      confirmLabel: "Yes, Hide It",
    };
  }, [isFinishing]);

  // ── Early Return (Must be AFTER all hooks) ────────────────────────────────
  if (!isVisible) return null;

  const isLastStep = currentStepIndex === totalSteps - 1;
  const isFirstStep = currentStepIndex === 0;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleNavigate = () => {
    navigate(currentStep.primaryUrl);
    setIsMinimized(true);
  };

  const handlePrev = () => {
    prevStep();
    const prevIdx = Math.max(0, currentStepIndex - 1);
    const prevUrl = steps[prevIdx]?.primaryUrl;
    if (prevUrl) navigate(prevUrl);
  };

  const handleNext = () => {
    if (isLastStep) {
      setIsFinishing(true);
      setShowDismissDialog(true);
      return;
    }
    nextStep();
    const nextIdx = Math.min(steps.length - 1, currentStepIndex + 1);
    const nextUrl = steps[nextIdx]?.primaryUrl;
    if (nextUrl) navigate(nextUrl);
  };

  const handleCloseIconClick = () => {
    setIsFinishing(false);
    setShowDismissDialog(true);
  };

  const handleDismissConfirm = () => {
    dismiss();
    setShowDismissDialog(false);
  };

  // ── Minimized Pill ────────────────────────────────────────────────────────
  if (isMinimized) {
    return (
      <div
        className={cn(
          "fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg cursor-pointer bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
        )}
        onClick={() => setIsMinimized(false)}
        role="button"
        aria-label="Open getting started guide"
      >
        <Rocket className="h-4 w-4" />
        <span>Getting Started</span>
        <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">
          {currentStepIndex + 1}/{totalSteps}
        </Badge>
      </div>
    );
  }

  // ── Desktop Style ─────────────────────────────────────────────────────────
  const desktopStyle: React.CSSProperties | undefined =
    isDesktop && activeItemRect
      ? {
          position: "fixed" as const,
          left: `${(activeItemRect.left + 10) + activeItemRect.width + 12}px`,
          top: `${Math.max(16, Math.min(activeItemRect.top + activeItemRect.height / 2 - 160, window.innerHeight - 380))}px`,
          width: "320px",
          zIndex: 50,
        }
      : undefined;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px]" aria-hidden="true" />

      {isDesktop && activeItemRect && positionReady && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: `${activeItemRect.left + activeItemRect.width}px`,
            top: `${activeItemRect.top + activeItemRect.height / 2 - 1}px`,
            width: "12px",
            height: "2px",
            background: "hsl(var(--primary))",
          }}
        />
      )}

      <div
        className={cn(
          "bg-background border border-border shadow-lg flex flex-col",
          !isDesktop && "fixed bottom-0 left-0 right-0 z-50 mx-0 rounded-t-xl",
          isDesktop && positionReady && "rounded-xl",
          isDesktop && !positionReady && "opacity-0 pointer-events-none fixed -left-[9999px]"
        )}
        style={desktopStyle}
        role="complementary"
        aria-label="Getting started guide"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-border">
          <div className="flex items-center gap-2">
            <Rocket className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Getting Started</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => setIsMinimized(true)}
              aria-label="Minimise guide"
            >
              <Minimize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              onClick={handleCloseIconClick}
              aria-label="Dismiss guide"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-1 px-4 pt-2">
          {steps.map((step, idx) => (
            <button
              key={step.id || idx}
              onClick={() => goToStep(idx)}
              className={cn(
                "h-1.5 rounded-full transition-all duration-200 focus:outline-none",
                idx === currentStepIndex ? "w-6 bg-primary" : idx < currentStepIndex ? "w-2 bg-primary/40" : "w-2 bg-muted"
              )}
            />
          ))}
          <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
            {currentStepIndex + 1} / {totalSteps}
          </span>
        </div>

        {/* Content */}
        <div className="px-4 py-3 flex-1">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 mb-1.5">
            {currentStep?.section}
          </Badge>
          <p className="font-semibold text-sm leading-tight mb-1">{currentStep?.title}</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{currentStep?.description}</p>
        </div>

        {/* Actions */}
        <div className="px-4 pb-3 pt-1 flex items-center gap-2 border-t border-border mt-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handlePrev}
            disabled={isFirstStep}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="default"
            size="sm"
            className="flex-1 h-7 text-xs gap-1"
            onClick={handleNavigate}
          >
            {currentStep?.actionLabel}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant={isLastStep ? "default" : "outline"}
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleNext}
          >
            {isLastStep ? <CheckCircle2 className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Dynamic Dismissal Confirmation Dialog */}
      <AlertDialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogContent.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDismissConfirm} disabled={isDismissing}>
              {isDismissing ? "Saving…" : dialogContent.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}